import { CloseOutlined, PlusOutlined } from "@ant-design/icons";
import { useEffect, useRef, useState } from "react";
import { useDarkMode, useThemeColors, useAppSelector, useAppDispatch } from "redux/hooks";
import { useSession } from "components/wrappers/SessionProvider";
import { UpdateSettingsAction } from "redux/actions/UpdateSettings";
import { ConditionalTooltip } from "./ConditionalTooltip";
import { Input, notification } from "antd";

export function QueriesView(props: {
    isOpen: boolean;
    onClose: () => void;
    initialQuery?: string;
    initialQueryName?: string;
}) {
    const cardRef = useRef<HTMLDivElement>(null);
    const darkMode = useDarkMode();
    const themeColors = useThemeColors();
    const dispatch = useAppDispatch();
    const session = useSession();
    const existingQueries = useAppSelector(state => state.user.settings?.queries || {});
    const [queryName, setQueryName] = useState(props.initialQueryName || "");
    const [query, setQuery] = useState(props.initialQuery || "");
    const [isSaving, setIsSaving] = useState(false);

    // Reset form when modal opens/closes or initialQuery changes
    useEffect(() => {
        if (props.isOpen) {
            setQueryName(props.initialQueryName || "");
            setQuery(props.initialQuery || "");
        }
    }, [props.isOpen, props.initialQuery, props.initialQueryName]);

    // Handle clicking outside the card
    const { isOpen, onClose } = props;
    useEffect(() => {
        if (!isOpen) return;

        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;

            // Don't close if clicking on the Ant Design modal or its overlay
            if (target.closest('.ant-modal') ||
                target.closest('.ant-modal-mask') ||
                target.closest('.ant-modal-wrap') ||
                target.closest('.ant-modal-content')) {
                return;
            }

            if (cardRef.current && !cardRef.current.contains(target)) {
                onClose();
            }
        };

        // Add event listener with a small delay to prevent immediate closing
        const timeoutId = setTimeout(() => {
            document.addEventListener("mousedown", handleClickOutside);
        }, 0);

        return () => {
            clearTimeout(timeoutId);
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isOpen, onClose]);

    const handleSave = async () => {
        if (!queryName.trim()) {
            notification.error({
                message: 'Query name required',
                description: 'Please enter a name for this query.',
                placement: 'bottomLeft',
                duration: 3
            });
            return;
        }

        if (!query.trim()) {
            notification.error({
                message: 'Query required',
                description: 'Please enter a query.',
                placement: 'bottomLeft',
                duration: 3
            });
            return;
        }

        setIsSaving(true);
        try {
            const updatedQueries = {
                ...existingQueries,
                [queryName.trim()]: query.trim()
            };

            // If we're editing and the name changed, remove the old entry
            if (props.initialQueryName && props.initialQueryName !== queryName.trim()) {
                delete updatedQueries[props.initialQueryName];
            }

            await dispatch(UpdateSettingsAction(session, { queries: updatedQueries }));

            notification.success({
                message: props.initialQueryName ? 'Query updated' : 'Query saved',
                description: `Query "${queryName.trim()}" has been ${props.initialQueryName ? 'updated' : 'saved'}.`,
                placement: 'bottomLeft',
                duration: 2
            });

            setQueryName("");
            setQuery("");
            props.onClose();
        } catch (error) {
            notification.error({
                message: 'Failed to save query',
                description: error instanceof Error ? error.message : 'Please try again.',
                placement: 'bottomLeft',
                duration: 3
            });
        } finally {
            setIsSaving(false);
        }
    };

    const handleCopy = (queryText: string) => {
        navigator.clipboard.writeText(queryText);
        notification.success({
            message: 'Query copied',
            description: 'Query has been copied to clipboard.',
            placement: 'bottomLeft',
            duration: 2
        });
    };

    if (!props.isOpen) {
        return null;
    }

    return (
        <>
            <style>{`
                .queries-input::placeholder {
                    color: ${themeColors.textColorMuted} !important;
                }
                .queries-textarea::placeholder {
                    color: ${themeColors.textColorMuted} !important;
                }
                .queries-textarea textarea {
                    height: 100% !important;
                }
            `}</style>
            {/* Overlay */}
            <div
                onClick={props.onClose}
                style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                    zIndex: 2000,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                {/* Queries Card */}
                <div
                    ref={cardRef}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        backgroundColor: themeColors.backgroundColor,
                        borderRadius: "8px",
                        boxShadow: darkMode ? "0 4px 12px rgba(0, 0, 0, 0.5)" : "0 4px 12px rgba(0, 0, 0, 0.15)",
                        width: "90vw",
                        height: "90vh",
                        position: "relative",
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    {/* Header with close button */}
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "16px 20px",
                            borderBottom: `1px solid ${themeColors.borderColor}`,
                        }}
                    >
                        <h2 style={{ margin: 0, fontSize: "1.5em", fontWeight: "600", color: themeColors.textColor }}>
                            {props.initialQueryName ? "Edit Query" : "Add Query"}
                        </h2>
                        <ConditionalTooltip title="Close queries">
                            <button
                                onClick={props.onClose}
                                style={{
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                    padding: "4px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: themeColors.iconColor,
                                    fontSize: "18px",
                                }}
                            >
                                <CloseOutlined />
                            </button>
                        </ConditionalTooltip>
                    </div>

                    {/* Content area */}
                    <div
                        style={{
                            padding: "20px",
                            flex: 1,
                            overflow: "auto",
                            display: "flex",
                            flexDirection: "column",
                            gap: "20px",
                        }}
                    >
                        {/* Add Query Form */}
                        <div style={{ display: "flex", flexDirection: "column", flex: 1, gap: "12px", minHeight: 0 }}>
                            <div>
                                <label style={{
                                    display: "block",
                                    marginBottom: "8px",
                                    color: themeColors.textColor,
                                    fontWeight: "500"
                                }}>
                                    Name
                                </label>
                                <Input
                                    value={queryName}
                                    onChange={(e) => setQueryName(e.target.value)}
                                    placeholder="Enter a name for this query..."
                                    onPressEnter={handleSave}
                                    disabled={isSaving}
                                    className="queries-input"
                                    style={{
                                        backgroundColor: darkMode ? themeColors.backgroundColorSecondary : themeColors.backgroundColor,
                                        color: themeColors.textColor,
                                        borderColor: themeColors.borderColorSecondary
                                    }}
                                />
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
                                <label style={{
                                    display: "block",
                                    marginBottom: "8px",
                                    color: themeColors.textColor,
                                    fontWeight: "500"
                                }}>
                                    Query
                                </label>
                                <div style={{ flex: 1, minHeight: 0, display: "flex" }}>
                                    <Input.TextArea
                                        value={query}
                                        onChange={(e) => setQuery(e.target.value)}
                                        placeholder="Enter SQL query..."
                                        disabled={isSaving}
                                        className="queries-textarea"
                                        style={{
                                            backgroundColor: darkMode ? themeColors.backgroundColorSecondary : themeColors.backgroundColor,
                                            color: themeColors.textColor,
                                            borderColor: themeColors.borderColorSecondary,
                                            fontFamily: "monospace",
                                            fontSize: "0.9em",
                                            resize: "none",
                                            flex: 1,
                                            height: "100%"
                                        }}
                                    />
                                </div>
                            </div>
                            <div style={{ display: "flex", justifyContent: "flex-end", flexShrink: 0 }}>
                                <button
                                    onClick={handleSave}
                                    disabled={isSaving || !queryName.trim() || !query.trim()}
                                    style={{
                                        background: (isSaving || !queryName.trim() || !query.trim()) ? themeColors.borderColorSecondary : themeColors.primaryColor,
                                        border: `1px solid ${(isSaving || !queryName.trim() || !query.trim()) ? themeColors.borderColorSecondary : themeColors.primaryColor}`,
                                        borderRadius: "4px",
                                        padding: "8px 16px",
                                        cursor: (isSaving || !queryName.trim() || !query.trim()) ? "not-allowed" : "pointer",
                                        color: "#ffffff",
                                        fontSize: "14px",
                                        fontWeight: "500",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px",
                                        transition: "all 0.2s"
                                    }}
                                    onMouseEnter={(e) => {
                                        if (!isSaving && queryName.trim() && query.trim()) {
                                            e.currentTarget.style.background = themeColors.primaryColorHover;
                                            e.currentTarget.style.borderColor = themeColors.primaryColorHover;
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (!isSaving && queryName.trim() && query.trim()) {
                                            e.currentTarget.style.background = themeColors.primaryColor;
                                            e.currentTarget.style.borderColor = themeColors.primaryColor;
                                        }
                                    }}
                                >
                                    <PlusOutlined />
                                    Save Query
                                </button>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </>
    );
}

